import { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  type: 'bot' | 'user';
  text: string;
  timestamp: Date;
  options?: string[];
}

type ChatStep = 'greeting' | 'name' | 'contact' | 'intent' | 'which_service' | 'summarize_service' | 'service' | 'complete';

const serviceSummaries: Record<string, string> = {
  'Pintura': 'O nosso serviço de pintura profissional foca-se na qualidade do acabamento e na durabilidade. Utilizamos tintas de alta gama e preparamos minuciosamente as superfícies para garantir um resultado impecável, seja em interiores ou exteriores.',
  'Pavimento Flutuante': 'Instalamos pavimentos flutuantes com precisão milimétrica. Oferecemos isolamento térmico e acústico superior, com uma vasta gama de acabamentos que unem a beleza da madeira à praticidade do sistema de clique.',
  'Pavimento Cerâmico': 'Especialistas em assentamento de cerâmica e porcelanato. Ideal para zonas de grande tráfego e humidade, combinando resistência extrema com designs modernos, nivelamento perfeito e acabamentos refinados.',
  'Instalação de Móveis': 'Montagem profissional de móveis de cozinha, roupeiros e mobiliário em geral. Garantimos que tudo fica perfeitamente nivelado, fixo e operacional, com ajuste fino de portas e gavetas.',
  'Móveis por Medida': 'Criamos peças únicas que se adaptam perfeitamente ao seu espaço. Desde roupeiros embutidos a cozinhas personalizadas, maximizamos cada centímetro da sua casa com materiais nobres e design exclusivo.',
  'Remodelações Gerais': 'Gestão completa da sua obra de renovação. Coordenamos todas as etapas, desde demolições e eletricidade até aos acabamentos finais, garantindo o cumprimento de prazos e a sua total satisfação.',
  'Outro': 'Temos uma equipa versátil pronta para qualquer desafio em carpintaria e manutenção. Diga-nos o que precisa e encontraremos a solução técnica ideal para o seu projeto.'
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStep, setCurrentStep] = useState<ChatStep>('greeting');
  const [userData, setUserData] = useState({
    name: '',
    telefone: '',
    service: ''
  });
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [hasShownScrollMessage, setHasShownScrollMessage] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Avatar image
  const avatarUrl = '/images/Atendente_chatbot_feminino.webp';
  const chatButtonImage = '/images/Atendente_chatbot_feminino.webp';
  const whatsappNumber = '351964759517';

  useEffect(() => {
    if (isOpen && currentStep === 'greeting' && messages.length === 0) {
      // Initialize chat with greeting when opened for the first time
      const greetingMessage: Message = {
        id: Date.now().toString(),
        type: 'bot',
        text: 'Olá! Sou a Marta, assistente da MM Carpintaria. Como posso ajudá-lo hoje? Para começarmos, qual é o seu nome?',
        timestamp: new Date()
      };
      setMessages([greetingMessage]);
      setCurrentStep('name');
    }
  }, [isOpen, currentStep, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, currentStep]);

  // Detect when user scrolls to "Fale agora" section
  useEffect(() => {
    const section = document.getElementById('fale-agora-section');

    if (!section) return;

    if (hasShownScrollMessage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.2) {
            setShowNotification(true);
            setHasShownScrollMessage(true);

            setTimeout(() => {
              setShowNotification(false);
            }, 6000);

            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '-50px'
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, [hasShownScrollMessage]);

  const validateContact = (contact: string): boolean => {
    // Check for email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(contact)) return true;

    // Check for phone
    const cleaned = contact.replace(/[\s\-\(\)]/g, '');
    return /^[\d\+]{9,15}$/.test(cleaned);
  };

  const addBotMessage = (text: string, options?: string[]) => {
    const botMessage: Message = {
      id: Date.now().toString(),
      type: 'bot',
      text,
      timestamp: new Date(),
      options
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const addUserMessage = (text: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
  };

  const serviceOptions = [
    'Pintura',
    'Pavimento Flutuante',
    'Pavimento Cerâmico',
    'Instalação de Móveis',
    'Móveis por Medida',
    'Remodelações Gerais',
    'Outro'
  ];

  const sendEmailNotification = async (data: typeof userData) => {
    try {
      const formData = new FormData();
      formData.append('_subject', `Novo Lead via Chat - ${data.service}`);
      formData.append('_template', 'table');
      formData.append('_captcha', 'false');
      formData.append('Nome', data.name);
      formData.append('Contacto', data.telefone);
      formData.append('Serviço', data.service);
      formData.append('Origem', 'Chatbot');

      await fetch('https://formsubmit.co/ajax/mmcarpintaria2023@gmail.com', {
        method: 'POST',
        body: formData
      });
    } catch (error) {
      console.error('Erro ao enviar notificação de chat:', error);
    }
  };

  const processUserMessage = async (text: string) => {
    addUserMessage(text);

    // Process based on current step
    switch (currentStep) {
      case 'name':
        setUserData(prev => ({ ...prev, name: text }));
        addBotMessage(`Prazer em conhecê-lo, ${text}! Para podermos entrar em contacto, poderia deixar o seu telemóvel ou e-mail?`);
        setCurrentStep('contact');
        break;

      case 'contact':
        if (!validateContact(text)) {
          addBotMessage('Por favor, insira um telemóvel ou e-mail válido para que possamos responder.');
          return;
        }
        setUserData(prev => ({ ...prev, telefone: text }));
        addBotMessage('Obrigado! O que gostaria de fazer agora?', ['Fazer um orçamento', 'Saber mais sobre um serviço']);
        setCurrentStep('intent');
        break;

      case 'intent':
        if (text === 'Fazer um orçamento') {
          addBotMessage('Com certeza! Qual serviço pretende orçamentar?', serviceOptions);
          setCurrentStep('service');
        } else if (text === 'Saber mais sobre um serviço') {
          addBotMessage('Sobre qual serviço gostaria de saber mais?', serviceOptions);
          setCurrentStep('which_service');
        } else {
          addBotMessage('Pode escolher uma das opções abaixo:', ['Fazer um orçamento', 'Saber mais sobre um serviço']);
        }
        break;

      case 'which_service':
        const summary = serviceSummaries[text] || serviceSummaries['Outro'];
        setUserData(prev => ({ ...prev, service: text }));
        addBotMessage(`${summary}\n\nGostaria de aproveitar e solicitar um orçamento gratuito para este serviço?`, ['Sim, por favor', 'Agora não, obrigado']);
        setCurrentStep('summarize_service');
        break;

      case 'summarize_service':
        if (text === 'Sim, por favor') {
          setIsSending(true);
          // Send email with current data
          await sendEmailNotification(userData);
          await new Promise(resolve => setTimeout(resolve, 1000));
          setIsSending(false);
          addBotMessage(`Excelente, ${userData.name}! Recebemos o seu pedido para "${userData.service}". A nossa equipa entrará em contacto em breve para dar seguimento ao seu orçamento.`);
          setCurrentStep('complete');
        } else {
          addBotMessage('Sem problemas! Se precisar de mais alguma coisa, estou por aqui. Obrigado!');
          setCurrentStep('complete');
        }
        break;

      case 'service':
        const updatedData = { ...userData, service: text };
        setUserData(prev => ({ ...prev, service: text }));
        setIsSending(true);
        // Send email with updated data
        await sendEmailNotification(updatedData);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsSending(false);
        addBotMessage('Obrigado pelas informações! A nossa equipa entrará em contacto em breve para dar seguimento ao seu pedido e agendar uma visita se necessário. Obrigado por escolher a MM Carpintaria!');
        setCurrentStep('complete');
        break;
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    const text = inputValue.trim();
    setInputValue('');
    processUserMessage(text);
  };

  const handleOptionClick = (option: string) => {
    processUserMessage(option);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetChat = () => {
    setMessages([]);
    setCurrentStep('greeting');
    setUserData({
      name: '',
      telefone: '',
      service: ''
    });
    setInputValue('');
    setHasShownScrollMessage(false);
    setShowNotification(false);
  };

  const toggleChat = () => {
    if (isOpen) {
      if (currentStep === 'complete') {
        resetChat();
      }
      setIsOpen(false);
    } else {
      if (currentStep === 'complete' || messages.length === 0) {
        resetChat();
      }
      setIsOpen(true);
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-28 right-6 z-50 w-16 h-16">
        {!isOpen && (
          <>
            <div className="absolute inset-0 rounded-full animate-ping-ring" style={{ animationDelay: '0s' }}>
              <div className="w-full h-full rounded-full bg-mmcarpintaria-green/20 border-2 border-mmcarpintaria-green/40"></div>
            </div>
          </>
        )}

        {!isOpen && (
          <button
            onClick={toggleChat}
            className="relative w-16 h-16 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 transform overflow-hidden scale-100 hover:scale-110 bg-white border-2 border-mmcarpintaria-green active:scale-95"
            aria-label="Abrir chat"
          >
            <img
              src={chatButtonImage}
              alt="Chat"
              className="w-full h-full object-cover rounded-full relative z-10"
            />
          </button>
        )}
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${whatsappNumber}?text=Olá,%20gostaria%20de%20solicitar%20um%20orçamento`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-[#25D366] hover:bg-[#20BA5A] shadow-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 active:scale-95"
        aria-label="Contatar via WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl text-white"></i>
      </a>

      {/* Notification Balloon */}
      {showNotification && (
        <div className="fixed bottom-44 right-6 z-50 max-w-xs animate-fade-in">
          <div className="bg-white rounded-lg shadow-2xl p-4 border border-gray-200 relative">
            <button
              onClick={() => setShowNotification(false)}
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
            >
              <i className="ri-close-line text-sm text-gray-600"></i>
            </button>
            <div className="flex items-start gap-3 pr-6">
              <img
                src={avatarUrl}
                alt="Atendente"
                className="w-10 h-10 rounded-full border-2 border-mmcarpintaria-green object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800 mb-1">Assistente MM</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Olá! Alguma dúvida sobre os nossos serviços de carpintaria ou remodelação?
                </p>
              </div>
            </div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white"></div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-[60] w-[440px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[80vh] bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex flex-col transition-all duration-300 transform origin-bottom-right ${isOpen
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
          }`}
      >
        {/* Chat Header */}
        <div className="bg-gradient-to-r from-mmcarpintaria-green to-mmcarpintaria-green-dark text-white p-5 rounded-t-xl flex items-center gap-4 relative shadow-md">
          <button
            onClick={toggleChat}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <i className="ri-close-line text-xl text-white"></i>
          </button>
          <div className="relative">
            <img
              src={avatarUrl}
              alt="Atendente"
              className="w-12 h-12 rounded-full border-2 border-white object-cover"
            />
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">Marta - MM Carpintaria</h3>
            <p className="text-xs text-white/80 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              Online agora
            </p>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-5 bg-[#F8FAFC] space-y-5 scrollbar-thin scrollbar-thumb-gray-200">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-message-in`}
            >
              <div
                className={`max-w-[85%] rounded-2xl p-4 shadow-sm ${message.type === 'user'
                  ? 'bg-mmcarpintaria-green text-white rounded-tr-none'
                  : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                  }`}
              >
                <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.text}</p>
                <div className={`text-[10px] mt-2 flex justify-end ${message.type === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                  {message.timestamp.toLocaleTimeString('pt-PT', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {message.options && (
                  <div className="mt-4 flex flex-col gap-2">
                    {message.options.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleOptionClick(option)}
                        className="text-sm bg-gray-50 border border-mmcarpintaria-green/30 text-mmcarpintaria-green-dark px-4 py-2.5 rounded-xl hover:bg-mmcarpintaria-green hover:text-white hover:border-mmcarpintaria-green transition-all duration-200 text-left font-medium active:scale-[0.98]"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isSending && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-mmcarpintaria-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-mmcarpintaria-green rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-mmcarpintaria-green rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-5 border-t border-gray-100 bg-white rounded-b-xl">
          {currentStep !== 'complete' ? (
            <div className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Escreva sua mensagem..."
                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-mmcarpintaria-green/20 focus:border-mmcarpintaria-green transition-all text-sm"
                disabled={isSending}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isSending}
                className="w-12 h-12 bg-mmcarpintaria-green text-white rounded-xl hover:bg-mmcarpintaria-green-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-mmcarpintaria-green/20 active:scale-90"
              >
                <i className="ri-send-plane-2-fill text-xl"></i>
              </button>
            </div>
          ) : (
            <div className="text-center py-2">
              <button
                onClick={resetChat}
                className="text-mmcarpintaria-green hover:text-mmcarpintaria-green-dark text-sm font-bold flex items-center justify-center gap-2 mx-auto hover:underline"
              >
                <i className="ri-refresh-line"></i>
                Iniciar nova conversa
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
